import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, finalize, map, tap } from 'rxjs';
import { APP_CONFIG, IAppConfig } from '../../../app.config';
import { DataService } from '../../../core/service/base/data.service';
import { MessageService } from '../../../core/service/message.service';
import { ProgressBarService } from '../../../core/service/progress-bar.service';
import { ErrandType } from '../../../round-planning/model/const/enums';
import {
  ErrandAssignmentOptions,
  ErrandTag,
  ErrandTagAccessGroupSelection,
  ErrandTagSavePayload,
  ErrandTagSelection,
  PropertyErrandTagResponsibility,
} from '../../model/errand-tag';

@Injectable({ providedIn: 'root' })
export class ErrandTagService extends DataService<ErrandTag, ErrandTagSavePayload, ErrandTag, ErrandTag> {
  constructor(
    client: HttpClient,
    @Inject(APP_CONFIG) config: IAppConfig,
    messages: MessageService,
    progressBar: ProgressBarService
  ) {
    super(client, config, messages, ErrandTag, progressBar, 'errand-tag');
  }

  getAll(): Observable<ErrandTag[]> {
    return this.getList();
  }

  getAccessGroups(): Observable<ErrandTagAccessGroupSelection[]> {
    return this.client.get<ErrandTagAccessGroupSelection[]>(`${this.endpoint}/access-groups`);
  }

  protected override createModel(modelData: unknown): ErrandTag {
    return new ErrandTag(modelData as Partial<ErrandTag>);
  }

  createNew(model: ErrandTagSavePayload, options = { notifyUser: true }): Observable<ErrandTag> {
    this.progressBar.start();
    return this.client.post<ErrandTag>(this.endpoint, model).pipe(
      map(item => new ErrandTag(item)),
      tap(() => {
        if (options.notifyUser) this.notification.showSuccess();
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  updateExisting(model: ErrandTag, options = { notifyUser: true }): Observable<ErrandTag> {
    this.progressBar.start();
    return this.client.put<ErrandTag>(this.endpoint, model.toSavePayload()).pipe(
      map(item => new ErrandTag(item)),
      tap(() => {
        if (options.notifyUser) this.notification.showSuccess();
      }),
      finalize(() => this.progressBar.stop())
    );
  }

  getAssignable(): Observable<ErrandTagSelection[]> {
    return this.client.get<ErrandTagSelection[]>(`${this.endpoint}-selection`);
  }

  getAccessibleSelections(): Observable<ErrandTagSelection[]> {
    return this.client.get<ErrandTagSelection[]>(`${this.endpoint}-selection/accessible`);
  }

  getAccessible(id: string): Observable<ErrandTagSelection> {
    return this.client.get<ErrandTagSelection>(`${this.endpoint}-selection/${id}`);
  }

  getAssignmentOptions(
    propertyId: string,
    errandTagId: string,
    errandType: ErrandType,
    executorGroupId?: string,
    applyConfiguredResponsibility = false
  ): Observable<ErrandAssignmentOptions> {
    let params = new HttpParams()
      .set('propertyId', propertyId)
      .set('errandType', `${errandType}`);
    if (errandTagId) params = params.set('errandTagId', errandTagId);
    if (executorGroupId) params = params.set('executorGroupId', executorGroupId);
    if (applyConfiguredResponsibility) params = params.set('applyConfiguredResponsibility', 'true');
    return this.client.get<ErrandAssignmentOptions>(`${this.endpoint.replace('/errand-tag', '')}/errand-assignment-options`, { params });
  }

  getResponsibilities(propertyId: string): Observable<PropertyErrandTagResponsibility[]> {
    return this.client.get<PropertyErrandTagResponsibility[]>(
      `${this.endpoint.replace('/errand-tag', '')}/property/${propertyId}/errand-tag-responsibility`
    );
  }

  saveResponsibilities(
    propertyId: string,
    rows: PropertyErrandTagResponsibility[]
  ): Observable<PropertyErrandTagResponsibility[]> {
    return this.client.put<PropertyErrandTagResponsibility[]>(
      `${this.endpoint.replace('/errand-tag', '')}/property/${propertyId}/errand-tag-responsibility`,
      rows
    );
  }
}
